import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useSites } from '../../../context/SiteContext';
import { useReportVisits } from '../../../hooks/useReportVisits';
import { buildPages, buildSpiders } from '../../../lib/analytics';
import { formatDateTime, formatNumber } from '../../../lib/format';
import { detectSpider } from '../../../lib/parse';
import { visitPagePath } from '../../../lib/source';
import EmptyState from '../../../components/dashboard/EmptyState';
import PageHeader from '../../../components/dashboard/PageHeader';
import StatTable from '../../../components/dashboard/StatTable';
import ExportButton from '../../../components/dashboard/ExportButton';

export default function SpidersPage() {
  const { currentSite } = useSites();
  const { spiderRanged, range } = useReportVisits(currentSite);
  const spiders = buildSpiders(spiderRanged);
  const pages = buildPages(spiderRanged).slice(0, 12);

  if (!currentSite) {
    return <EmptyState title="先添加一个站点" desc="添加站点后可统计搜索引擎蜘蛛抓取情况。" />;
  }

  return (
    <div>
      <PageHeader
        title="蜘蛛统计"
        desc="识别百度、谷歌、360、搜狗等爬虫，和正常访客数据分开计算。"
        range={range}
        extra={
          <ExportButton
            filename={`${currentSite.domain}-spiders.csv`}
            columns={['蜘蛛', '抓取次数']}
            rows={spiders.map((s) => [s.name, formatNumber(s.value)])}
          />
        }
      />

      <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5">
        <h2 className="text-sm font-semibold text-foreground-900">蜘蛛抓取量</h2>
        <div className="mt-4 h-64">
          {spiders.length === 0 ? (
            <p className="text-sm text-foreground-500">当前时段暂无蜘蛛访问</p>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={spiders}>
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} width={32} />
                <Tooltip />
                <Bar dataKey="value" fill="#C4A484" radius={[6, 6, 0, 0]} name="抓取次数" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
          <div className="px-5 py-3 border-b border-background-200">
            <h2 className="text-sm font-semibold text-foreground-900">蜘蛛明细</h2>
          </div>
          <StatTable
            columns={['蜘蛛', '抓取次数']}
            rows={spiders.map((s) => [s.name, formatNumber(s.value)])}
          />
        </div>
        <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
          <div className="px-5 py-3 border-b border-background-200">
            <h2 className="text-sm font-semibold text-foreground-900">被抓取页面</h2>
          </div>
          <StatTable
            columns={['页面', '抓取次数']}
            rows={pages.map((p) => [p.path, formatNumber(p.pv)])}
          />
        </div>
      </div>

      <div className="mt-4 rounded-lg border border-background-200 bg-background-50 overflow-hidden">
        <div className="px-5 py-3 border-b border-background-200">
          <h2 className="text-sm font-semibold text-foreground-900">最近抓取记录</h2>
        </div>
        <StatTable
          columns={['时间', '蜘蛛', '抓取页面']}
          rows={spiderRanged.slice(0, 20).map((v) => [
            formatDateTime(v.createdAt),
            detectSpider(v.userAgent),
            visitPagePath(v.pageUrl),
          ])}
        />
      </div>
    </div>
  );
}
